import React, { useEffect, useMemo, useState } from "react";
import Lightbox from "yet-another-react-lightbox";
import Counter from "yet-another-react-lightbox/plugins/counter";
import { useTranslation } from "react-i18next";
import { FaExpand } from "react-icons/fa";
import "yet-another-react-lightbox/styles.css";
import "yet-another-react-lightbox/plugins/counter.css";
import "./GalleryGrid.css";
import ScrollReveal, { revealItemProps } from "../common/Reveal";
import { isRtlLanguage } from "../../utils/i18n";

function normalizeGalleryImage(image, index, description) {
  if (typeof image === "string") {
    return {
      id: `${description}-${index}`,
      src: image,
      alt: `${description} ${index + 1}`,
    };
  }

  const src = image?.url || image?.src || "";
  const alt = image?.alt || `${description} ${index + 1}`;

  return {
    id: image?.id || `${description}-${index}`,
    src,
    alt,
    title: image?.title || "",
    width: image?.width || undefined,
    height: image?.height || undefined,
  };
}

function GalleryGrid({ images, descriptionKey }) {
  const { t, i18n } = useTranslation();
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const description = t(descriptionKey);
  const isAr = isRtlLanguage(i18n.language);

  const normalizedImages = useMemo(
    () => images.map((image, index) => normalizeGalleryImage(image, index, description)),
    [images, description],
  );

  const slides = useMemo(
    () =>
      normalizedImages.map((image) => ({
        src: image.src,
        alt: image.alt,
      })),
    [normalizedImages],
  );

  useEffect(() => {
    setLightboxOpen(false);
    setActiveIndex(0);
  }, [images]);

  const openLightbox = (index) => {
    setActiveIndex(index);
    setLightboxOpen(true);
  };

  if (normalizedImages.length === 0) {
    return (
      <p className="gallery-grid__empty" role="status">
        {t("projects.noImages")}
      </p>
    );
  }

  return (
    <>
      <ScrollReveal stagger className="gallery-grid">
        {normalizedImages.map((image, index) => (
          <button
            key={image.id}
            type="button"
            {...revealItemProps(index, "gallery-card group")}
            onClick={() => openLightbox(index)}
            aria-label={image.alt}
          >
            <img
              src={image.src}
              alt={image.alt}
              title={image.title || undefined}
              loading="lazy"
              decoding="async"
              className="gallery-card__image"
              width={image.width}
              height={image.height}
            />
            <div className="gallery-card__overlay">
              <FaExpand className="gallery-card__icon" aria-hidden="true" />
              <p className="gallery-card__title text-start">
                {image.title || description}
              </p>
              <p className="gallery-card__hint text-start">{t("Click here")}</p>
            </div>
          </button>
        ))}
      </ScrollReveal>

      <Lightbox
        open={lightboxOpen}
        close={() => setLightboxOpen(false)}
        index={activeIndex}
        slides={slides}
        plugins={[Counter]}
        counter={{ container: { style: { top: "unset", bottom: 0 } } }}
        rtl={isAr}
        carousel={{ finite: false }}
        controller={{ closeOnBackdropClick: true }}
        on={{ view: ({ index }) => setActiveIndex(index) }}
      />
    </>
  );
}

GalleryGrid.revealManaged = true;

export default GalleryGrid;
